<script setup lang="ts">
import { ref, computed } from 'vue'
import { useIntersectionObserver } from '@vueuse/core'
import type { Schemas } from '#shopware'

interface CmsElementCustomerBuy {
    id: string
    type: 'customer-buy' | 'customer_buy'
    slot: string
    versionId?: string
    config: Record<string, never>
    data?: {
        product?: Schemas['Product']
    }
}

const props = defineProps<{
    content: CmsElementCustomerBuy
}>()

const { product } = useProduct(
    props.content?.data?.product ?? undefined
)

const productId = computed(() => product.value?.parentId ?? product.value?.id ?? null)


const { apiClient } = useShopwareContext()

const customerBuysProducts = ref<Schemas['Product'][]>([])
const isLoading = ref(false)
const hasLoaded = ref(false)
const elementRef = ref<HTMLElement | null>(null)

const loadCustomerBuysProducts = async (id: string) => {
    isLoading.value = true
    try {
        const result = await apiClient.invoke('readCustomerBuysProducts POST /also-bought/{productId}', {
            pathParams: { productId: id },
            body: {
                includes: {
                    product: [
                        'id',
                        'name',
                        'translated',
                        'parentId',
                        'productNumber',
                        'markAsTopseller',
                        'customFields',
                        'calculatedPrice',
                        'calculatedPrices',
                        'calculatedCheapestPrice',
                        'cover',
                        'coverId',
                        'manufacturer',
                        'manufacturerId',
                        'seoUrls',
                    ],
                    product_media: ['media', 'mediaId'],
                    media: ['url', 'alt', 'title'],
                    product_manufacturer: ['id', 'name', 'translated'],
                    seo_url: ['seoPathInfo', 'pathInfo'],
                },
                associations: {
                    cover: {
                        associations: {
                            media: {}
                        }
                    },
                    manufacturer: {},
                    seoUrls: {},
                }
            }
        })

        customerBuysProducts.value = result.data.elements ?? []
    } catch {
        customerBuysProducts.value = []
    } finally {
        isLoading.value = false
    }
}


const { stop } = useIntersectionObserver(
    elementRef,
    (entries) => {
        const entry = entries[0]
        if (entry?.isIntersecting && !hasLoaded.value && productId.value) {
            hasLoaded.value = true
            loadCustomerBuysProducts(productId.value)
            stop()
        }
    },
    {
        threshold: 0.1,
        rootMargin: '200px'
    }
)


const shouldRender = computed(() => {
    return productId.value && customerBuysProducts.value.length > 0
})
</script>

<template>
    <div
        v-if="productId"
        ref="elementRef"
        class="cms-element-customer-buy"
    >
        <ComponentProductCarousel
            v-if="shouldRender"
            :id="`customer-buy-${content.id}`"
            :products="customerBuysProducts"
            :title="$t('product.customerBuy')"
            :show-title="true"
            layout-type="minimal"
            :items-per-slide="{
                default: 1,
                md: 2,
                lg: 3,
                xl: 4
            }"
            :gap="10"
            show-navigation="outside"
            :show-indicators="false"
            :auto-play="false"
            :loop="false"
            :show-wishlist="true"
            :show-badges="true"
        />
    </div>
</template>
